import {
    AppId,
    Button,
    ButtonType,
    FCX,
    getFormattedThousand, ID, ProductLogo, sanitize, Tooltip,
    usePlatformContext
} from "@datassential/platform-ui-lib";
import './SectionReports.scss';
import FoodSection from "@pages/Food/components/Section/Section";
import FoodWidget from "@pages/Food/components/Widget/Widget";
import { IFoodProfileDocuments } from "@models/FoodProfileDocuments";
import TileSubTitle from "@components/ReportProTile/TileSubTitle";
import { useCallback, useEffect } from "react";
import { FoodSectionEnum } from "@pages/Food/Food";

const FoodSectionReports: FCX<{
    word: string;
    data: IFoodProfileDocuments['reports'];
    isLoading: boolean;
    setHasData: (hasData: boolean) => void;
}> = ({
    word,
    data: {
        data: {
            reports,
            totalCount,
        }
    },
    isLoading,
    setHasData,
}) => {
    const {
        userInfo: {
            products,
            reportProDetailsUrl,
            reportProMoreResultsUrl,
        },
    } = usePlatformContext();
    const productRP = products[AppId.ReportPro];

    const getItemTooltipId = useCallback(
        (id: ID) => `FP-reports-item-${id}`,
        [],
    );

    const detailsPath = process.env.REACT_APP_RP_ITEM_LINK ?? reportProDetailsUrl;
    const moreResultsPath = process.env.REACT_APP_RP_SEARCH_LINK ?? reportProMoreResultsUrl;

    useEffect(() => {
        setHasData(!isLoading && totalCount > 0);
    }, [isLoading, totalCount, setHasData]);

    if (isLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (!totalCount) return null;

    return (
        <div
            className="Food__section"
            id={FoodSectionEnum.Reports}
            data-testid="blockFoodSectionReports"
        >
            <FoodSection
                headerContent={(
                    <div data-testid="textFoodReportsTitle">
                        REPORTS
                    </div>
                )}
                className="appears-top del-1"
                testId="blockFoodReports"
            >
                <FoodWidget className="FoodSectionReports">
                    <div className="FoodSectionReports__content">
                        {reports.map((report, index) => (
                            <div
                                key={report.id}
                                className="FoodSectionReports__item"
                                data-tooltip-id={getItemTooltipId(report.id)}
                                data-testid={`buttonFoodReportsItem-${index}`}
                            >
                                <img
                                    src={report.thumbnail}
                                    alt={report.title}
                                    className="FoodSectionReports__item-thumbnail"
                                />
                                <a
                                    className="FoodSectionReports__item-link"
                                    target="_blank"
                                    rel="noreferrer"
                                    href={sanitize(`${productRP.url}${detailsPath}${report.id}`)}
                                    data-testid={`buttonFoodReportsItemExternalLink-${index}`}
                                />
                                <div className="FoodSectionReports__item-tooltip-anchor"/>
                            </div>
                        ))}

                        <Button
                            modifiers={['no-rem']}
                            type={ButtonType.Anchor}
                            link={sanitize(`${productRP.url}${moreResultsPath}${word}`)}
                            className="FoodSectionReports__button"
                            data-testid="buttonFoodReportsAllResultsExternalLink"
                        >
                            view all {getFormattedThousand(totalCount)} documents
                        </Button>
                    </div>
                    {reports.map((report, index) => (
                        <Tooltip
                            key={report.id}
                            id={getItemTooltipId(report.id)}
                            place="top"
                        >
                            <div className="FoodSectionReports__item-tooltip">
                                <div
                                    className="FoodSectionReports__item-tooltip-title"
                                    data-testid={`textFoodReportsItemTitle-${index}`}
                                >
                                    {report.title}
                                </div>
                                <TileSubTitle
                                    data={report}
                                    testId={`textFoodReportsItemSubTitle-${index}`}
                                />
                                <div
                                    className="FoodSectionReports__item-tooltip-description"
                                    data-testid={`textFoodReportsItemDescription-${index}`}
                                >
                                    {report.description}
                                </div>
                            </div>
                        </Tooltip>
                    ))}
                </FoodWidget>
            </FoodSection>
        </div>
    );
};

export default FoodSectionReports;